/**
 * @file: ProductManifestManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { TaskWorkflowNodeManager } from '@euler/app/flows/task/functions/task-detail/TaskWorkflowNodeManager';
import {
  IProductManifestManager,
  ITaskManager,
} from '@euler/app/flows/task/functions/task-detail/types';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { isDebugEnabled } from '@euler/functions/debug';
import { writeContentsToCachedFile } from '@euler/lib/cache/cacheUtil';
import { IdType } from '@euler/model/common';
import {
  OrgUserRoleType,
  ProductAuthorType,
  TaskProductManifestItemOrigin,
} from '@euler/model/enum';
import { ProductAuthorInfo } from '@euler/model/product';
import {
  CachedTaskDetail,
  SaveTaskProductManifestItemInfo,
  SaveTaskProductManifestItemsPayload,
  SaveTaskProductManifestOriginInfo,
  TaskDetailVersions,
  TaskProductInfo,
  TaskProductManifest,
  TaskProductManifestItem,
  TaskProductManifestItemStaged,
  TaskProductManifestStaged,
  VersionedResponse,
} from '@euler/model/task-detail';
import { idEquals, makeDebug } from '@euler/utils';
import produce from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  debounceTime,
  map,
  skip,
} from 'rxjs';

const debug = makeDebug(
  'taskmanager:product-manifest',
  isDebugEnabled('productManifest'),
);

export class ProductManifestManager
  extends TaskWorkflowNodeManager
  implements IProductManifestManager
{
  readonly staged$: BehaviorSubject<TaskProductManifestStaged>;
  readonly manifest$: Observable<TaskProductManifestItem[]>;
  readonly dirty$: BehaviorSubject<boolean>;
  private changeSubscription?: Subscription;
  private detailSubscription?: Subscription;
  private readonly cacheFilePath: string;
  private _saved: TaskProductManifestStaged | undefined;

  constructor(readonly taskManager: ITaskManager) {
    super(taskManager);

    this.dirty$ = new BehaviorSubject<boolean>(false);

    this.cacheFilePath = this.taskManager.getCacheFilePath(
      'product-manifest.json',
    );

    this.staged$ = new BehaviorSubject<TaskProductManifestStaged>({
      items: [],
    });

    this.detailSubscription = this.taskManager.detail$.subscribe(detail => {
      this.onCachedDetailChange(detail);
    });

    this.manifest$ = this.staged$.pipe(
      map(x =>
        x.items
          .filter(item => item.id != null)
          .map<TaskProductManifestItem>(item => ({
            id: item.id!,
            subject: item.subject,
            product: item.product,
            qty: item.qty,
            author: item.author,
            closer: item.closer,
            jobRefs: item.jobRefs,
            origin: item.origin,
            confirmations: item.confirmations,
          })),
      ),
    );

    this.startObserveChanges();
  }

  addProductForSubject(
    subject: string,
    product: TaskProductInfo,
    origin: TaskProductManifestItemOrigin,
    bucket?: string,
  ) {
    return this.produceProductForSubject(
      subject,
      product.id,
      (items, index) => {
        if (index < 0) {
          items.push({
            subject,
            author: this.getCurrentAuthor(),
            product,
            qty: 1,
            bucket,
            jobRefs: undefined,
            origin,
            confirmations: undefined,
          });
        } else if (items[index].removed) {
          items[index].removed = undefined;
        }
      },
    );
  }

  removeProductForSubject(subject: string, productId: IdType) {
    return this.produceProductForSubject(subject, productId, (items, index) => {
      if (index >= 0) {
        const item = items[index];
        if (item.id) {
          item.removed = true;
        } else {
          items.splice(index, 1);
        }
      }
    });
  }

  removeProductsOfBucket(bucket: string) {
    this.produceManifest(draft => {
      for (let i = draft.items.length - 1; i >= 0; i--) {
        const item = draft.items[i];
        if (item.bucket === bucket) {
          draft.items.splice(i, 1);
        }
      }
    });
  }

  produceManifest(
    recipe: (draft: WritableDraft<TaskProductManifestStaged>) => void,
  ) {
    const staged = this.staged$.getValue();
    const updated = produce(staged, recipe);
    if (updated !== staged) {
      this.staged$.next(updated);
      this.dirty$.next(true);
      return true;
    }
    return false;
  }

  async clear() {
    const resp = await this.taskService.clearTaskProductManifestItems(
      this.taskNo,
    );
    this.handleVersionedResponse(resp, (_, { productManifest }) => {
      productManifest.items = [];
    });
    this.dirty$.next(false);
  }

  // Change the closer on a single manifest item. The BE is the
  // source of truth; this call writes through and then mirrors the new
  // closer into the local staged copy + the cached detail so the UI
  // reflects it without a full refetch.
  async changeManifestItemCloser(
    manifestItemId: IdType,
    closer: ProductAuthorInfo,
  ): Promise<void> {
    const resp = await this.taskService.changeProductManifestItemCloser(
      this.taskNo,
      manifestItemId,
      closer,
    );
    this.handleVersionedResponse(resp, (_, { productManifest }) => {
      const cached = productManifest.items.find(x =>
        idEquals(x.id, manifestItemId),
      );
      if (cached != null) cached.closer = closer;
    });
    this.produceManifest(draft => {
      const staged = draft.items.find(
        x => x.id != null && idEquals(x.id, manifestItemId),
      );
      if (staged != null) staged.closer = closer;
    });
  }

  beginUpdate() {
    this._saved = this.staged$.getValue();
  }

  endUpdate() {
    this._saved = undefined;
  }

  cancelUpdate() {
    if (this._saved) {
      this.staged$.next(this._saved);
      this._saved = undefined;
    }
  }

  getSavePayload(
    origin: SaveTaskProductManifestOriginInfo,
  ): SaveTaskProductManifestItemsPayload {
    return this.stagedToPayload(origin);
  }

  async save(options: {
    payload?: SaveTaskProductManifestItemsPayload;
    origin: SaveTaskProductManifestOriginInfo;
  }) {
    if (!this.dirty$.value && !options?.payload) {
      return;
    }
    const payload = options?.payload ?? this.stagedToPayload(options.origin);
    const resp = await this.taskService.saveProductManifestItems(
      this.taskNo,
      payload,
    );
    this.handleVersionedResponse(resp, (_, { response, productManifest }) => {
      productManifest.items = response;
    });
    this.dirty$.next(false);
  }

  cleanUp() {
    this.stopObserveChanges();

    this.detailSubscription?.unsubscribe();
    this.detailSubscription = undefined;
  }

  dispose() {
    this.cleanUp();
  }

  private onCachedDetailChange(detail: CachedTaskDetail) {
    if (detail.productManifest == null) {
      return;
    }
    const staged = this.toStaged(detail.productManifest);
    if (staged !== this.staged$.getValue()) {
      this.staged$.next(staged);
    }
  }

  private startObserveChanges() {
    if (this.changeSubscription) {
      return;
    }

    debug('changes observer started');
    this.changeSubscription = this.staged$
      .pipe(skip(1), debounceTime(500))
      .subscribe(staged => {
        void writeContentsToCachedFile(
          this.cacheFilePath,
          JSON.stringify(staged, null, 2),
        );
        debug('product manifest written to cache');
      });
  }

  private stopObserveChanges() {
    if (this.changeSubscription) {
      this.changeSubscription.unsubscribe();
      this.changeSubscription = undefined;
      debug('changes observer stopped');
    }
  }

  private toStaged(
    productManifest: TaskProductManifest,
  ): TaskProductManifestStaged {
    const current = this.staged$.getValue();
    return produce(current, draft => {
      // remove items that do not exist any more.
      const ids = new Set(productManifest.items.map(x => String(x.id)));
      for (let i = draft.items.length - 1; i >= 0; i--) {
        const item = draft.items[i];
        if (item.removed || (item.id && !ids.has(String(item.id)))) {
          draft.items.splice(i, 1);
        }
      }

      for (const item of productManifest.items) {
        const existing = draft.items.find(
          x =>
            x.subject === item.subject &&
            idEquals(x.product.id, item.product.id),
        );
        if (existing == null) {
          draft.items.push({
            id: item.id,
            subject: item.subject,
            product: item.product,
            qty: item.qty,
            author: item.author,
            closer: item.closer,
            jobRefs: item.jobRefs,
            origin: item.origin,
            confirmations: item.confirmations,
          });
        } else {
          // clear associated bucket identifier
          if (existing.bucket) {
            existing.bucket = undefined;
          }

          if (
            existing.jobRefs?.length !== item.jobRefs?.length ||
            existing.jobRefs?.some(jobId => !item.jobRefs?.includes(jobId)) ||
            item.jobRefs?.some(jobId => !existing.jobRefs?.includes(jobId))
          ) {
            existing.jobRefs = item.jobRefs;
          }

          if (!existing.id) {
            existing.id = item.id;
          }
          if (
            !idEquals(
              existing.product.sourceContractId,
              item.product.sourceContractId,
            )
          ) {
            existing.product.sourceContractId = item.product.sourceContractId;
          }

          // Sync closer drift from the server (auto-attributed on first
          // adoption, mutated by change-closer calls).
          if (
            !idEquals(item.closer?.id ?? 0, existing.closer?.id ?? 0) ||
            item.closer?.name !== existing.closer?.name ||
            item.closer?.type !== existing.closer?.type
          ) {
            existing.closer = item.closer ?? undefined;
          }
        }
      }
    });
  }

  private getCurrentAuthor(): ProductAuthorInfo {
    const roleHelper = new OrgUserRoleHelper(this.currentUser.role);
    return {
      id: this.currentUser.memberId,
      name: this.currentUser.name,
      type: roleHelper.hasRole(OrgUserRoleType.ServiceAgents)
        ? ProductAuthorType.ServiceAgent
        : ProductAuthorType.Technicians,
    };
  }

  private stagedToPayload(
    origin: SaveTaskProductManifestOriginInfo,
  ): SaveTaskProductManifestItemsPayload {
    const staged = this.staged$.getValue();
    return {
      author: this.getCurrentAuthor(),
      origin,
      items: staged.items.map<SaveTaskProductManifestItemInfo>(x => ({
        subject: x.subject,
        contractId: x.product.sourceContractId,
        productId: x.product.id,
        remove: x.removed,
        qty: x.qty,
      })),
    };
  }

  private produceProductForSubject(
    subject: string,
    productId: IdType,
    recipe: (
      draft: WritableDraft<TaskProductManifestItemStaged[]>,
      index: number,
    ) => void,
  ) {
    return this.produceManifest(draft => {
      const index = draft.items.findIndex(
        x => x.subject === subject && idEquals(x.product.id, productId),
      );
      recipe(draft.items, index);
    });
  }

  private handleVersionedResponse<U>(
    versionedResponse: VersionedResponse<U>,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        response: U;
        productManifest: TaskProductManifest;
        version: number;
        original: TaskProductManifest;
      },
    ) => void,
  ) {
    return this.produceWithVersions(
      versionedResponse.versions,
      (draft, context) => {
        recipe(draft, { ...context, response: versionedResponse.response });
      },
    );
  }

  private produceWithVersions(
    versions: TaskDetailVersions,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      context: {
        productManifest: TaskProductManifest;
        version: number;
        original: TaskProductManifest;
      },
    ) => void,
  ) {
    const original = this.cachedDetail.productManifest;
    const version = versions.productManifest;

    if (
      version === original.version &&
      this.cachedDetail.version === versions.detail
    ) {
      // nothing changed
      return this.cachedDetail;
    }

    return this.taskManager.produceWithVersions(versions, draft => {
      const { productManifest } = draft;
      recipe(draft, {
        productManifest,
        version,
        original,
      });
      if (productManifest.version !== version) {
        productManifest.version = version;
      }
    });
  }
}
